import React, { useState, useContext, useEffect, useMemo } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import JournalEntryForm from "../components/JournalEntryForm";
import JournalEntryList from "../components/JournalEntryList";
import DayScoreCardModal from "../components/DayScoreCardModal";
import { UserContext } from "../App";
import Spinner from '../components/MatrixLoader';
import { ArrowLeftIcon, ChevronLeftIcon, ChevronRightIcon, PhotoIcon } from '@heroicons/react/24/outline';
import { isTapeReading } from "../utils/tradeHelpers";
import { cleanCreatedTimestamp } from "../utils/dateUtils";
import { buildDayCapitalLookup, getDayPercentage } from "../utils/dayCapital";
import { getMonthArrowOffset, shouldIgnoreMonthArrowKey } from "../utils/calendarKeyboard";
import { getAdjacentJournalDay, summarizeDayTrades } from "../utils/dayNavigation";
import { DEFAULT_BE_THRESHOLD, sanitizeBeThreshold } from "../statsUtils";
import { formatCurrencyAdaptive } from "../utils/numberFormat";

const formatSignedCurrency = (value) => {
  const numeric = Number(value) || 0;
  return `${numeric > 0 ? '+' : numeric < 0 ? '-' : ''}${formatCurrencyAdaptive(Math.abs(numeric))}`;
};

const dayNavButtonClass = "inline-flex h-8 w-8 sm:h-9 sm:w-9 items-center justify-center rounded-full border border-[#E5E7EB] dark:border-[#374151] bg-white/90 dark:bg-[#0F172A]/80 text-[#111827] dark:text-[#F9FAFB] shadow-subtle transition-colors hover:bg-[#F3F4F6] dark:hover:bg-[#1F2937] shrink-0";

// Helper function to extract timestamp from created field
function extractTimestamp(created) {
  return cleanCreatedTimestamp(created);
}

// Used until the full account history loads.
function calculateDayPercentageFromTrades(trades) {
  if (!trades.length) return null;

  const sorted = [...trades].sort((a, b) => new Date(extractTimestamp(a.created)) - new Date(extractTimestamp(b.created)));
  const dayPnl = sorted.reduce((sum, entry) => sum + (Number(entry.pnl) || 0), 0);
  const firstTrade = sorted[0];
  const firstBalance = Number(firstTrade?.accountBalance);
  const firstPnl = Number(firstTrade?.pnl);

  if (!Number.isFinite(firstBalance) || !Number.isFinite(firstPnl)) {
    return dayPnl > 0 ? 999999 : 0;
  }
  const startingCapital = firstBalance - firstPnl;
  if (startingCapital <= 0) return dayPnl > 0 ? 999999 : 0;
  return (dayPnl / startingCapital) * 100;
}

function calculateAverageRr(trades) {
  if (!trades.length) return null;
  const values = trades
    .map((entry) => Number(entry.rr))
    .filter((value) => Number.isFinite(value));
  if (!values.length) return null;
  return values.reduce((sum, value) => sum + value, 0) / values.length;
}

function getLatestFieldValue(trades, fieldName) {
  if (!trades.length) return null;
  const sorted = [...trades].sort((a, b) => new Date(extractTimestamp(b.created)) - new Date(extractTimestamp(a.created)));
  const match = sorted.find((entry) => entry[fieldName] !== undefined && entry[fieldName] !== null && String(entry[fieldName]).trim() !== "");
  return match ? match[fieldName] : null;
}

const DayPage = () => {
  const { currentUser, selectedAccount, dataRefreshTrigger } = useContext(UserContext);
  const { month, day, year: urlYearParam } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const urlYear = urlYearParam; // Get year from URL if present
  const [entries, setEntries] = useState([]);
  const [accountEntries, setAccountEntries] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [loading, setLoading] = useState(true);
  const [returnPath, setReturnPath] = useState(null);
  const [isDayCardOpen, setIsDayCardOpen] = useState(false);
  const [showDayCardCta, setShowDayCardCta] = useState(false);
  const [beThreshold, setBeThreshold] = useState(DEFAULT_BE_THRESHOLD);

  // Check if we should show the form directly (from Quick Add)
  useEffect(() => {
    if (location.state?.directAdd) {
      setShowForm(true);
      setReturnPath(location.state?.returnPath || '/');
    }
    if (location.state?.showDayCardCta) {
      setShowDayCardCta(true);
    }
  }, [location.state]);

  useEffect(() => {
    if (!showDayCardCta) return undefined;
    const timeoutId = setTimeout(() => setShowDayCardCta(false), 4000);
    return () => clearTimeout(timeoutId);
  }, [showDayCardCta]);

  useEffect(() => {
    if (!currentUser || !selectedAccount || !month || !day) return;
    const fetchEntries = async () => {
      setLoading(true);
      const { db } = await import('../firebase');
      const { collection, query, where, getDocs } = await import('firebase/firestore');
      const entriesCol = collection(db, 'users', currentUser.uid, 'accounts', selectedAccount.id, 'entries');
      // Include year filter to ensure we get entries for the correct year (including weekends)
      // Use year from URL, or fallback to current year
      const yearToUse = urlYear || new Date().getFullYear().toString();
      const q = query(
        entriesCol, 
        where('year', '==', yearToUse),
        where('month', '==', month), 
        where('day', '==', day)
      );
      const snap = await getDocs(q);
      const data = snap.docs.map(doc => ({ ...doc.data(), id: doc.id }));
      // Sort by created timestamp in descending order (newest first)
      const sortedData = data.sort((a, b) => {
        const aTimestamp = extractTimestamp(a.created);
        const bTimestamp = extractTimestamp(b.created);
        return new Date(bTimestamp) - new Date(aTimestamp);
      });
      setEntries(sortedData);
      setLoading(false);
    };
    fetchEntries();
  }, [currentUser, selectedAccount, urlYear, month, day, dataRefreshTrigger]);

  // The day's percentage needs the account's full funding history, not just this day.
  useEffect(() => {
    if (!currentUser || !selectedAccount) return undefined;
    let cancelled = false;
    setAccountEntries(null);
    const fetchAccountEntries = async () => {
      try {
        const { db } = await import('../firebase');
        const { collection, getDocs } = await import('firebase/firestore');
        const snap = await getDocs(collection(db, 'users', currentUser.uid, 'accounts', selectedAccount.id, 'entries'));
        if (!cancelled) setAccountEntries(snap.docs.map(doc => ({ ...doc.data(), id: doc.id })));
      } catch (error) {
        console.error('DayPage: error loading account history:', error);
      }
    };
    fetchAccountEntries();
    return () => { cancelled = true; };
  }, [currentUser, selectedAccount, dataRefreshTrigger]);

  // Break-even threshold for the win-rate line (same preference the calendar uses).
  useEffect(() => {
    if (!currentUser?.uid) return undefined;
    let cancelled = false;
    const loadThreshold = async () => {
      try {
        const { db } = await import('../firebase');
        const { doc, getDoc } = await import('firebase/firestore');
        const prefsSnap = await getDoc(doc(db, 'users', currentUser.uid, 'preferences', 'settings'));
        if (!cancelled && prefsSnap.exists()) setBeThreshold(sanitizeBeThreshold(prefsSnap.data().beThreshold));
      } catch (error) {
        console.error('DayPage: error loading break-even threshold:', error);
      }
    };
    loadThreshold();
    return () => { cancelled = true; };
  }, [currentUser]);

  // Determine the year to display: URL param > entry year > current year
  const year = urlYear || (() => {
    if (entries.length > 0) {
      // Use the year from the first entry (most recent)
      const entryYear = entries[0]?.year;
      if (entryYear) {
        return parseInt(entryYear, 10);
      }
    }
    return new Date().getFullYear();
  })();

  const goToAdjacentDay = (direction) => {
    if (!month || !day) return;
    const target = getAdjacentJournalDay(accountEntries || [], { year, month, day }, direction);
    navigate(`/day/${target.year}/${target.month}/${target.day}`);
  };

  // ← / → step to the previous/next day with entries (ignored while typing or with a dialog open).
  const dayNavBlocked = showForm || isDayCardOpen || location.state?.directAdd === true;
  useEffect(() => {
    const handleKeyDown = (event) => {
      const offset = getMonthArrowOffset(event.key);
      if (!offset || dayNavBlocked || shouldIgnoreMonthArrowKey(event)) return;
      event.preventDefault();
      goToAdjacentDay(offset);
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  });

  const dayStats = useMemo(() => summarizeDayTrades(entries, beThreshold), [entries, beThreshold]);

  if (!month || !day) {
    console.warn("DayPage: missing month or day param", { month, day });
    return <div className="text-[#EF4444] p-8">Invalid day URL. (Missing month or day param)</div>;
  }

  // Calculate initialAccountBalance for the next entry
  let initialAccountBalance = "";
  if (entries.length > 0) {
    // Since entries are sorted newest first, entries[0] is the most recent entry chronologically
    const mostRecent = entries[0];
    // Use the account balance from the most recent entry directly
    initialAccountBalance = (Number(mostRecent.accountBalance) || 0).toFixed(2);
  }

  const handleAddEntry = async (savedEntry) => {
    if (!currentUser || !selectedAccount) return;
    
    // Add the saved entry to the local state immediately
    if (savedEntry) {
      setEntries(prevEntries => {
        // Add the new entry to the existing entries and sort by created timestamp (newest first)
        const newEntries = [...prevEntries, savedEntry];
        return newEntries.sort((a, b) => {
          const aTimestamp = extractTimestamp(a.created);
          const bTimestamp = extractTimestamp(b.created);
          return new Date(bTimestamp) - new Date(aTimestamp);
        });
      });
    }
  };

  const calculateDayPercentage = (trades) => {
    if (!trades.length) return null;
    if (!accountEntries) return calculateDayPercentageFromTrades(trades);
    const capital = buildDayCapitalLookup(accountEntries, selectedAccount)(year, month, day);
    const dayPnl = trades.reduce((sum, entry) => sum + (Number(entry.pnl) || 0), 0);
    return getDayPercentage(dayPnl, capital);
  };

  const buildDayCardPayload = (sourceEntries) => {
    const dayEntriesForCard = sourceEntries.filter((entry) => !entry.isDeposit && !entry.isPayout && !entry.isResetExcluded);
    const dayTradesForCard = dayEntriesForCard.filter((entry) => !isTapeReading(entry));
    return {
      dateKey: `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`,
      dateLabel: new Date(Number(year), Number(month) - 1, Number(day)).toLocaleDateString('en-US', {
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      }),
      pnl: dayTradesForCard.reduce((sum, entry) => sum + (Number(entry.pnl) || 0), 0),
      rr: calculateAverageRr(dayTradesForCard),
      percentage: calculateDayPercentage(dayTradesForCard),
      session: getLatestFieldValue(dayTradesForCard, "tradeSession"),
      grade: getLatestFieldValue(dayTradesForCard, "tradeRating"),
    };
  };

  // If directAdd is true, hide the DayPage content (form will show as overlay)
  const isDirectAdd = location.state?.directAdd === true;
  const dayEntries = entries.filter((entry) => !entry.isDeposit && !entry.isPayout && !entry.isResetExcluded);
  const dayTrades = dayEntries.filter((entry) => !isTapeReading(entry));
  const dayPnl = dayTrades.reduce((sum, entry) => sum + (Number(entry.pnl) || 0), 0);
  const dayRr = calculateAverageRr(dayTrades);
  const dayPercentage = calculateDayPercentage(dayTrades);
  const daySession = getLatestFieldValue(dayTrades, "tradeSession");
  const dayGrade = getLatestFieldValue(dayTrades, "tradeRating");
  const dayDateKey = `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
  const dayDateLabel = new Date(Number(year), Number(month) - 1, Number(day)).toLocaleDateString('en-US', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
  return (
    <div className="page-shell relative animate-fadeIn">
      {!isDirectAdd && (
        <div className="max-w-full overflow-y-auto h-auto md:h-[calc(100vh-80px)]">
          <div className="absolute left-0 top-3 z-30">
            <button
              className="flex items-center gap-2 ml-2 sm:ml-6 px-3 sm:px-3.5 py-2 bg-white dark:bg-[#1F2937] hover:bg-[#F3F4F6] dark:hover:bg-[#374151] text-sm text-[#111827] dark:text-[#F9FAFB] border border-[#E5E7EB] dark:border-[#374151] rounded-card shadow-subtle transition-colors"
              onClick={() => navigate(`/month/${year}/${month}`)}
            >
              <ArrowLeftIcon className="w-4 h-4" />
              Back
            </button>
          </div>
          
          {/* Big Bold Date Display */}
          <div className="w-full text-center mb-8 mt-14 sm:mt-4">
            <div className="flex items-center justify-center gap-2 sm:gap-3 mb-2">
              <button
                type="button"
                onClick={() => goToAdjacentDay(-1)}
                className={dayNavButtonClass}
                aria-label="Previous trading day"
                title="Previous trading day"
              >
                <ChevronLeftIcon className="h-4 w-4" />
              </button>
              <h1 className="min-w-0 text-2xl md:text-4xl font-semibold text-[#111827] dark:text-[#F9FAFB]">
                {new Date(year, month - 1, day).toLocaleDateString('en-US', {
                  weekday: 'long',
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric'
                })}
              </h1>
              <button
                type="button"
                onClick={() => goToAdjacentDay(1)}
                className={dayNavButtonClass}
                aria-label="Next trading day"
                title="Next trading day"
              >
                <ChevronRightIcon className="h-4 w-4" />
              </button>
            </div>
            {dayStats && (
              <p data-testid="day-stats-line" className="theme-text-muted text-xs sm:text-sm tabular-nums">
                {dayStats.count} trade{dayStats.count !== 1 ? 's' : ''}
                {dayStats.winRate !== null ? ` · ${Math.round(dayStats.winRate)}% win rate` : ''}
                {dayStats.count > 1
                  ? ` · best ${formatSignedCurrency(dayStats.best)} · worst ${formatSignedCurrency(dayStats.worst)}`
                  : ''}
              </p>
            )}
          </div>
          
          <div className="sticky top-4 z-30 w-full flex justify-center mb-6">
            {!showForm && (
              <div className="flex flex-col items-center gap-3">
                <div className="flex items-center gap-2">
                  <button
                    className="btn-primary"
                    onClick={() => setShowForm(true)}
                  >
                    Add Entry
                  </button>
                  <button
                    type="button"
                    className="inline-flex items-center gap-2 px-4 py-2 bg-white dark:bg-[#1F2937] hover:bg-[#F3F4F6] dark:hover:bg-[#374151] text-[#111827] dark:text-[#F9FAFB] border border-[#E5E7EB] dark:border-[#374151] rounded-card shadow-subtle transition-colors"
                    onClick={() => setIsDayCardOpen(true)}
                  >
                    <PhotoIcon className="w-4 h-4" />
                    Day Card
                  </button>
                </div>
              </div>
            )}
          </div>
          {loading ? <div className="flex justify-center items-center py-24"><Spinner size={48} /></div> : <JournalEntryList entries={entries.filter(entry => !entry.isResetExcluded)} />}
        </div>
      )}
      {showForm && (
        <JournalEntryForm 
          onSave={(savedEntry) => {
            handleAddEntry(savedEntry);
            setShowForm(false);
            const updatedEntries = [savedEntry, ...entries];
            const dayCardPayload = buildDayCardPayload(updatedEntries);
            setShowDayCardCta(true);
            // If we came from Quick Add, navigate to the day page using the entry's date
            if (returnPath) {
              navigate(returnPath, {
                replace: true,
                state: {
                  showDayCardToast: true,
                  dayCardPayload,
                }
              });
            }
          }} 
          onCancel={() => {
            setShowForm(false);
            // If we came from Quick Add, navigate back to previous page
            if (returnPath) {
              navigate(returnPath);
            }
          }}
          onGoToQuick={(entryMode) => {
            navigate('/quick-add', {
              state: {
                returnPath: returnPath || `/day/${year}/${month}/${day}`,
                selectedDate: dayDateKey,
                entryMode,
              },
            });
          }}
          initialAccountBalance={initialAccountBalance}
          initialYear={year}
          initialMonth={month}
          initialDay={day}
          initialJournalMode={location.state?.journalMode || null}
        />
      )}
      <DayScoreCardModal
        isOpen={isDayCardOpen}
        onClose={() => setIsDayCardOpen(false)}
        dateKey={dayDateKey}
        dateLabel={dayDateLabel}
        pnl={dayPnl}
        rr={dayRr}
        percentage={dayPercentage}
        session={daySession}
        grade={dayGrade}
      />
      {showDayCardCta && !showForm && (
        <button
          type="button"
          onClick={() => {
            setIsDayCardOpen(true);
            setShowDayCardCta(false);
          }}
          className="fixed top-4 left-1/2 -translate-x-1/2 z-[95] inline-flex items-center px-4 py-3 rounded-card border border-[#BFDBFE] dark:border-[#1E3A8A] bg-[#EFF6FF] dark:bg-[#0F172A] text-[#1D4ED8] dark:text-[#93C5FD] shadow-elevated hover:opacity-90 transition-opacity"
        >
          <span className="text-sm font-semibold">View day image</span>
        </button>
      )}
    </div>
  );
};

export default DayPage; 
